/**
 * RotateOverlay — Total-Redesign-Spec §1b rotate interstitial.
 *
 * Shown over the play screen when the current game's manifest declares an
 * `orientation` ('landscape' | 'portrait') that contradicts the device's
 * live matchMedia orientation. Dimmed overlay + animated rotating-phone
 * glyph; after 4s a "Play anyway" button appears that dismisses the
 * overlay for the rest of the round (cover layout means the game still
 * functions, just sub-optimally).
 *
 * No game declares `orientation` yet, so this renders nothing today — it
 * activates automatically as manifests gain the field.
 */
import { useEffect, useState } from 'react';
import { gameOrientation } from './manifests';

interface Props {
  gameId: string;
  /** Dismissal is scoped to the round — a new matchId re-arms the overlay. */
  matchId: string;
}

function useViewportOrientation(): 'landscape' | 'portrait' {
  const [o, setO] = useState<'landscape' | 'portrait'>(() =>
    window.matchMedia('(orientation: landscape)').matches ? 'landscape' : 'portrait');
  useEffect(() => {
    const mq = window.matchMedia('(orientation: landscape)');
    const onChange = (e: MediaQueryListEvent) => setO(e.matches ? 'landscape' : 'portrait');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return o;
}

export function RotateOverlay({ gameId, matchId }: Props) {
  const wanted = gameOrientation(gameId);
  const current = useViewportOrientation();
  const [dismissed, setDismissed] = useState(false);
  const [showPlayAnyway, setShowPlayAnyway] = useState(false);

  // Re-arm for each new round.
  useEffect(() => {
    setDismissed(false);
    setShowPlayAnyway(false);
  }, [matchId]);

  const mismatch = wanted !== null && wanted !== current;

  // "Play anyway" appears after 4s of continuous mismatch.
  useEffect(() => {
    if (!mismatch || dismissed) return;
    const t = window.setTimeout(() => setShowPlayAnyway(true), 4000);
    return () => window.clearTimeout(t);
  }, [mismatch, dismissed, matchId]);

  if (!mismatch || dismissed) return null;

  return (
    <div className="rotate-overlay" role="dialog" aria-label="Rotate your phone">
      <div className="rotate-overlay-card">
        <span className={`rotate-glyph rotate-glyph-${wanted}`} aria-hidden="true">📱</span>
        <p className="rotate-overlay-text">Rotate your phone</p>
        <p className="rotate-overlay-sub">
          {wanted === 'landscape' ? 'This game plays best sideways.' : 'This game plays best upright.'}
        </p>
        {showPlayAnyway && (
          <button
            type="button"
            className="rotate-overlay-skip"
            onClick={() => setDismissed(true)}
          >
            Play anyway
          </button>
        )}
      </div>
    </div>
  );
}
